import { IOrderManagementOrderListProps } from "@/api_framework/api_modals/Orders";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useOrderManagementCancelReasonAPI } from "@/app/hooks/api_hooks/orders/useOrderCancelReasonAPI";
import { useOrderManualAssignListAPI } from "@/app/hooks/api_hooks/orders/useOrderManualAssignListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { IOrderManagementState, setIsOrderSearchActive, setOrderFilter } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import config from "@/configs/development";

export interface IBookingFiltersFormFields {
  startDate: string;
  endDate: string;
  clinics: Array<string>;
}

firebase.initializeApp(config.firebase);

const FilterComponent = ({ active, setActive, orderType, resetPage }) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const { orderclinicList, orderFilter, orderList, isCentralAdmin, isOrderSearchActive, orderModule } =
    routeState.state as IOrderManagementState;
  const fetchManualAssign = useOrderManualAssignListAPI();
  const fetchCancilationReson = useOrderManagementCancelReasonAPI();
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const dispatch = useAppDispatch();
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
  const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;
  const clinicItemsList = useSelectMenuReducer(orderclinicList, "name", "id");

  const formHook = useForm<IBookingFiltersFormFields>({
    mode: "onChange",
    defaultValues: {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
  });
  const selectedClinics = formHook.watch("clinics") || [];

  useEffect(() => {
    if (orderclinicList?.length > 0) {
      formHook.setValue(
        "clinics",
        clinicItemsList?.map((i) => i.id),
      );
    }
  }, [orderclinicList]);

  useEffect(() => {
    if (orderFilter?.clinic_id && orderFilter?.start_date) return;
    if (!clinicItemsList?.length) return;

    const selectedClinicIds = (formHook.getValues("clinics") || clinicItemsList?.map((item) => item.id))
      ?.map((id) => id?.trim())
      ?.filter(Boolean);

    if (!selectedClinicIds?.length) return;

    const defaultFilter: IOrderManagementOrderListProps = {
      clinic_id: selectedClinicIds.join(","),
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };

    dispatch(setOrderFilter({ data: defaultFilter }));
    resetPage?.();
    setActive(orderType === "Rx" ? "0" : "3");
  }, [
    orderFilter?.clinic_id,
    orderFilter?.start_date,
    clinicItemsList,
    formHook,
    formattedStartDate,
    formattedEndDate,
    dispatch,
    resetPage,
    setActive,
    orderType,
  ]);

  useEffect(() => {
    if (!isCentralAdmin) {
      const currentTime = Date.now() / 1000;
      orderList?.forEach((item) => {
        if (item?.order_details?.order_status === "confirmed") {
          const updatedTime = item?.updated_at;
          const timeDifference = (currentTime - updatedTime) / 60;
          if (timeDifference >= 5) {
            // playAudio();
          }
        }
      });
    }
  }, [orderList, isCentralAdmin, orderType]);

  // Initial list+stats fetch for a ready orderFilter is owned by StatsComponent's
  // own mount effect — fetching it here too caused duplicate/racing API calls.

  useEffect(() => {
    fetchManualAssign();
    fetchCancilationReson();
  }, []);

  const handelClick = () => {
    const formData = formHook.getValues();
    const selectedClinicIds = (formData?.clinics || []).map((id) => id?.trim()).filter(Boolean);
    const constructedBody: IOrderManagementOrderListProps = {
      clinic_id: selectedClinicIds.join(","),
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };
    dispatch(setOrderFilter({ data: constructedBody }));
    dispatch(setIsOrderSearchActive(false));
    resetPage?.();
    setActive(orderType === "Rx" ? "0" : "3");
  };

  const handelButtonDisable = () => {
    if (selectedClinics?.length === 0) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-start lg:w-auto lg:flex-nowrap lg:justify-end">
      <div className="w-full sm:w-auto">
        <OMCalendarComponent
          selectionDate={selectionDate}
          setSelectionDate={setSelectionDate}
          startDate={formattedStartDate}
          endDate={formattedEndDate}
          handelClick={() => undefined}
          isPaddingOnTop={false}

        />
      </div>
      {clinicItemsList?.length > 0 && (
        <div className="w-full sm:w-[280px] lg:w-[20vw]">
          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              if (items) {
                const clinicID = items.map((i) => i.id);
                formHook.setValue("clinics", clinicID);
              }
            }}
            withSelectAll
            defaultSelectAll={true}
            showTooltips
            withDropDownSearch
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.clinics}
            register={formHook.register("clinics", {
              required: true,
            })}
            showTypedErrors={false}
            containerClassName="w-full"
            className="h-9"
            placeholder="Select Clinics"
            defaultSelected={clinicItemsList?.filter((c) => selectedClinics?.includes(c.id))}
            selectItems={clinicItemsList}
          />
        </div>
      )}
      <SolidButton
        size="XL"
        onButtonClick={handelClick}
        disabled={handelButtonDisable()}
        className="h-9 w-full whitespace-nowrap bg-black text-white hover:bg-[#111827] disabled:cursor-not-allowed disabled:bg-[#D0D5DD] disabled:text-[#667085] sm:w-[120px]"
      >
        Apply Filter
      </SolidButton>
    </div>
  );
};

export default FilterComponent;
